import { surveyManagementService } from '@/services/surveyManagementService/surveyManagementService';
import { RootState } from '@/store';
import {
  CreateSurveyDto,
  CreateSurveyResponseDto,
  GetAllSurveysResponseDto,
  GetSurveyStatsResponseDto,
  languageIds,
  SurveyFilters,
  SurveyListItem,
  SurveyStats,
} from '@/types/survey.types';
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface SurveyState {
  list: {
    data: SurveyListItem[];
    total?: number;
    page?: number;
    limit?: number;
    totalPages?: number;
  };
  listbasedOnLanguage: {
    data: SurveyListItem[];
    total?: number;
    page?: number;
    limit?: number;
    totalPages?: number;
  };

  selected: SurveyListItem | null;
  surveyStats: SurveyStats | null;
  loading: boolean;
  loadingSurveyStats: boolean;
  error: string | null;
  SurveyFilterOptions: any[];

  page: number;
  pageSize: number;

  // ✅ filters
  filters: {
    search?: string;
    status?: boolean;
  };
}

const initialState: SurveyState = {
  list: {
    data: [],
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 0,
  },
  listbasedOnLanguage: {
    data: [],
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 0,
  },
  selected: null,
  surveyStats: null,
  loading: false,
  loadingSurveyStats: false,
  error: null,
  SurveyFilterOptions: [],

  page: 1,
  pageSize: 10,

  // ✅ default filters
  filters: {
    search: '',
    status: undefined,
  },
};

export const fetchSurveysList = createAsyncThunk<
  GetAllSurveysResponseDto,
  SurveyFilters | undefined,
  { rejectValue: string }
>('survey/fetchAll', async (filters, { rejectWithValue }) => {
  try {
    const response = await surveyManagementService.getSurveysList(filters);
    return response;
  } catch (error: any) {
    const apiMessage =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message ||
      'Error fetching surveys list';

    return rejectWithValue(apiMessage);
  }
});

export const getAllSurveyListBasedOnLanguages = createAsyncThunk<
  GetAllSurveysResponseDto,
  languageIds | undefined,
  { rejectValue: string }
>('survey/language', async (filters, { rejectWithValue }) => {
  try {
    const response = await surveyManagementService.getAllSurveysBasedOnLanguages(filters);

    return response;
  } catch (error: any) {
    const apiMessage =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message ||
      'Error fetching surveys list';

    return rejectWithValue(apiMessage);
  }
});

export const createSurvey = createAsyncThunk<
  CreateSurveyResponseDto,
  CreateSurveyDto,
  {
    rejectValue: string;
  }
>('survey/create', async (data, { rejectWithValue }) => {
  try {
    return await surveyManagementService.createSurvey(data);
  } catch (error: any) {
    const apiMessage =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message ||
      'Error creating survey';

    return rejectWithValue(apiMessage);
  }
});

export const getSurveyById = createAsyncThunk<
  SurveyListItem,
  string,
  {
    rejectValue: string;
  }
>('survey/getById', async (id: string, { rejectWithValue }) => {
  try {
    const response = await surveyManagementService.getSurveyById(id);
    return response.data as SurveyListItem;
  } catch (error: any) {
    const apiMessage =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message ||
      'Error getting Survey by Id';

    return rejectWithValue(apiMessage);
  }
});

export const getSurveyQuestions = createAsyncThunk<
  { id: string; questionsJson: any[] },
  string,
  {
    rejectValue: string;
  }
>('survey/getQuestions', async (id: string, { rejectWithValue }) => {
  try {
    const response = await surveyManagementService.getSurveyQuestions(id);
    return { id, questionsJson: response.data.questionsJson || [] };
  } catch (error: any) {
    const apiMessage =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message ||
      'Error fetching survey questions';

    return rejectWithValue(apiMessage);
  }
});

export const updateSurvey = createAsyncThunk<
  CreateSurveyResponseDto,
  { id: string; data: Partial<CreateSurveyDto> },
  { rejectValue: string }
>('survey/update', async ({ id, data }, { rejectWithValue }) => {
  try {
    return await surveyManagementService.updateSurvey(id, data);
  } catch (error: any) {
    const apiMessage =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message ||
      'Error updating Survey';

    return rejectWithValue(apiMessage);
  }
});

export const updateSurveyStatus = createAsyncThunk<
  CreateSurveyResponseDto,
  { id: string; data: Partial<CreateSurveyDto> },
  { rejectValue: string }
>('survey/updateStatus', async ({ id, data }, { rejectWithValue }) => {
  try {
    return await surveyManagementService.updateSurvey(id, data);
  } catch (error: any) {
    const apiMessage =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message ||
      'Error updating Survey Status';

    return rejectWithValue(apiMessage);
  }
});

export const deleteSurvey = createAsyncThunk<
  CreateSurveyResponseDto,
  { id: string; data: number },
  { rejectValue: string }
>('survey/delete', async ({ id, data }, { rejectWithValue }) => {
  try {
    return await surveyManagementService.deleteSurvey(id, data);
  } catch (error: any) {
    const apiMessage =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message ||
      'Error delete Survey';

    return rejectWithValue(apiMessage);
  }
});

export const getSurveyStats = createAsyncThunk<
  GetSurveyStatsResponseDto,
  void,
  {
    state: RootState;
    rejectValue: string;
  }
>('survey/stats', async (_, { rejectWithValue }) => {
  try {
    return await surveyManagementService.getSurveyStats();
  } catch (error: any) {
    const apiMessage =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message ||
      'Error get Survey Stats';

    return rejectWithValue(apiMessage);
  }
});

const surveySlice = createSlice({
  name: 'survey',
  initialState,
  reducers: {
    clearSurveyError(state) {
      state.error = null;
    },
    updateSurveyInList(
      state,
      action: PayloadAction<{ id: number; updates: Partial<SurveyListItem> }>,
    ) {
      const index = state.list.data.findIndex((item) => item.id === action.payload.id);
      if (index !== -1) {
        state.list.data[index] = {
          ...state.list.data[index],
          ...action.payload.updates,
        };
      }
    },
    updateSurveyQuestions(state, action: PayloadAction<{ id: string; questionsJson: any[] }>) {
      const index = state.list.data.findIndex((item) => item.id === Number(action.payload.id));
      if (index !== -1) {
        state.list.data[index] = {
          ...state.list.data[index],
          questionsJson: action.payload.questionsJson,
        };
      }
    },
    removeSurveyFromList(state, action: PayloadAction<number>) {
      state.list.data = state.list.data.filter((item) => item.id !== action.payload);
      if (state.list.total) state.list.total -= 1;
    },
    setFilters(state, action: PayloadAction<{ search?: string; status?: boolean }>) {
      state.filters = action.payload;
      state.list.page = 1; //  RESET PAGE HERE
      state.list.limit = 10;
    },
    setPage(state, action: PayloadAction<number>) {
      state.list.page = action.payload;
    },
    setPageSize(state, action: PayloadAction<number>) {
      state.list.limit = action.payload;
      state.list.page = 1;
    },
    clearSurveyListBasedOnLanguage(state) {
      state.listbasedOnLanguage = {
        data: [],
        total: 0,
        page: 1,
        limit: 10,
        totalPages: 0,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSurveysList.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchSurveysList.fulfilled, (state, action) => {
        state.loading = false;

        const { data, meta } = action.payload;
        state.list.data = data;
        state.list.total = meta?.total;
        state.list.page = meta?.page;
        state.list.limit = meta?.limit;
        state.list.totalPages = meta?.totalPages;
      })
      .addCase(fetchSurveysList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    builder.addCase(getSurveyById.fulfilled, (state) => {
      state.loading = false;
    });

    builder
      .addCase(getAllSurveyListBasedOnLanguages.pending, (state) => {
        state.loading = true;
      })
      .addCase(getAllSurveyListBasedOnLanguages.fulfilled, (state, action) => {
        state.loading = false;

        const { data, meta } = action.payload;
        state.listbasedOnLanguage.data = data;
        state.listbasedOnLanguage.total = meta?.total;
        state.listbasedOnLanguage.page = meta?.page;
        state.listbasedOnLanguage.limit = meta?.limit;
        state.listbasedOnLanguage.totalPages = meta?.totalPages;
      })
      .addCase(getAllSurveyListBasedOnLanguages.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.listbasedOnLanguage.data = [];
      });

    builder
      .addCase(createSurvey.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createSurvey.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(createSurvey.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(getSurveyStats.pending, (state) => {
        state.loadingSurveyStats = true;
      })
      .addCase(getSurveyStats.fulfilled, (state, action) => {
        state.loadingSurveyStats = false;
        state.surveyStats = action.payload.data;
      })
      .addCase(getSurveyStats.rejected, (state, action) => {
        state.loadingSurveyStats = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(updateSurvey.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateSurvey.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(updateSurvey.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(deleteSurvey.pending, (state) => {
        // state.loading = true;
        state.error = null;
      })
      .addCase(deleteSurvey.fulfilled, (state, action) => {
        // state.loading = false;
      })
      .addCase(deleteSurvey.rejected, (state, action) => {
        // state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(getSurveyQuestions.fulfilled, (state, action) => {
        const { id, questionsJson } = action.payload;
        const index = state.list.data.findIndex((item) => item.id === Number(id));
        if (index !== -1) {
          state.list.data[index] = {
            ...state.list.data[index],
            questionsJson,
          };
        }
      });
  },
});

export const {
  clearSurveyError,
  updateSurveyInList,
  removeSurveyFromList,
  updateSurveyQuestions,
  setFilters,
  setPage,
  setPageSize,
  clearSurveyListBasedOnLanguage,
} = surveySlice.actions;
export default surveySlice.reducer;
